import { Coordinate } from 'ol/coordinate';
import { orderBy, flatten, isEmpty } from 'lodash';
import { Mission, SamplingSpec, GridPatterns, Sample, SoilCore, Waypoint, CorePoint, SampleCentroid, Job } from '../db';
import { cleanSet, CircleShape, LineShape, BaseShape, rotatePoint } from '../utils';
import { FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB, ft_to_m } from '../constants';
import { fromLatLon, toLatLon } from 'utm';
import { IntermediateWaypointsMap, UtmLocation } from '../types/types';

// Smart-path tour application no longer flows through this generator —
// MissionClass.buildSmartPath bypasses calculate_path entirely and calls
// applySmartPathTourToExistingCores directly. This generator only handles
// the non-smart-path case (Update Path, plot generation, etc.).
class GridPathGenerator {
  private prevSampleId: string = '';
  private alternatedOnPreviousSample: boolean | null = null;

  constructor(
    private mission: Mission,
    private spec: SamplingSpec,
    private initialPrevPoint: Coordinate | null = null,
    private intermediateWaypointsMap: IntermediateWaypointsMap = {},
  ) {}

  generate() {
    // Calculate the shape of each cluster of samples.
    let subsamplesShape: BaseShape | null = null;

    if (this.spec.pattern_type === GridPatterns.CIRCLE) {
      subsamplesShape = new CircleShape(this.spec.cores, ft_to_m(this.spec.radius));
    } else if (this.spec.pattern_type === GridPatterns.LINE) {
      subsamplesShape = new LineShape(this.spec.cores, ft_to_m(this.spec.length), this.spec.angle);
    }

    if (this.spec.cores === 0) {
      throw new Error('No cores specified');
    }

    if (!subsamplesShape) {
      return;
    }

    const sampleGroups = this.groupSamplesWithCommonSampleSite();

    if (isEmpty(sampleGroups)) {
      return;
    }

    const orderedSampleGroups = orderBy(sampleGroups, (samples) => samples[0].order, ['asc']);

    orderedSampleGroups.forEach((group, i) => {
      const nextSampleUtmLoc = this.getNextSampleUtmLocation(orderedSampleGroups, i);
      this.generateCoresForSamplesGroup(group, nextSampleUtmLoc, subsamplesShape);
    });

    // Renumber waypoints
    for (const [i, waypoint] of orderBy(this.mission.getWaypoints(), ['waypoint_number'], ['asc']).entries()) {
      waypoint.waypoint_number = i + 1;
    }
  }

  private getNextSampleUtmLocation(orderedSampleGroups: Sample[][], i: number): UtmLocation | undefined {
    const nextGroup = orderedSampleGroups[i + 1];
    const nextGroupSample = nextGroup ? nextGroup[0] : undefined;
    const nextSampleCentroid = nextGroupSample?.getSampleCentroid();
    const nextSampleUtmLoc = nextSampleCentroid
      ? fromLatLon(nextSampleCentroid.lat, nextSampleCentroid.lon)
      : undefined;

    return nextSampleUtmLoc;
  }

  private generateCoresForSamplesGroup(
    group: Sample[],
    nextPointUtmLoc: UtmLocation | undefined,
    subsamplesShape: BaseShape,
  ) {
    const groupSamplesCount = group.length;

    let sampleNumberInGroup = 0;
    group.forEach((sample) => {
      const sampleSite = sample.getSampleSite();
      const sampleCentroid = sampleSite.getSampleCentroid();

      if (!sampleCentroid) {
        return;
      }

      const intermediateWaypoints: Coordinate[] = this.getIntermediateWaypointsBidirectional(
        this.prevSampleId,
        sample.sample_id!,
      );

      intermediateWaypoints.forEach((point: Coordinate) => {
        this.mission.addWaypoint(point[0], point[1]);
      });

      sampleNumberInGroup += 1;

      this.generateCores(
        sample,
        nextPointUtmLoc,
        this.mission,
        this.initialPrevPoint,
        this.chooseSampleLocation(sampleCentroid, groupSamplesCount, sampleNumberInGroup, this.spec.angle),
        subsamplesShape,
        this.prevSampleId,
      );

      this.prevSampleId = sample.sample_id!;
    });
  }

  private chooseSampleLocation(
    sampleCentroid: SampleCentroid,
    groupSamplesCount: number,
    sampleNumberInGroup: number,
    angle: number = 0,
  ) {
    const metersBetweenCoreLines = ft_to_m(
      this.spec.parallel_line_distance_ft !== undefined
        ? this.spec.parallel_line_distance_ft
        : FEET_BETWEEN_CORE_LINES_FOR_MULTI_LAB,
    );

    // If a group consists of a single sample - no movement is required.
    const sampleUtmLoc = fromLatLon(sampleCentroid.lat, sampleCentroid.lon);

    if (groupSamplesCount === 1) {
      return sampleUtmLoc;
    }

    // Save the original centroid position
    const centroidGeometryPoint = {
      x: sampleUtmLoc.easting,
      y: sampleUtmLoc.northing,
    };

    const evenNumberOfSamples = groupSamplesCount % 2 === 0;

    // If the number of samples in group is even, we want to move the sampleCentroid,
    // so that cores are generated evenly on each side of a centroid.
    // If the number of samples is odd, then we need to move the centroid,
    // so that cores in the middle lie on the middle sample line.
    const moveLineLengths =
      Math.floor(groupSamplesCount / 2) - (evenNumberOfSamples ? 0.5 : 0) - sampleNumberInGroup + 1;

    const moveMeters = moveLineLengths * metersBetweenCoreLines;

    // First move the sample the required distance.
    sampleUtmLoc.northing -= moveMeters;

    // Then rotate around the original centroid.
    const rotatedCenter = rotatePoint(
      {
        x: sampleUtmLoc.easting,
        y: sampleUtmLoc.northing,
      },
      centroidGeometryPoint,
      angle,
    );

    sampleUtmLoc.easting = rotatedCenter.x;
    sampleUtmLoc.northing = rotatedCenter.y;

    return sampleUtmLoc;
  }

  private groupSamplesWithCommonSampleSite(): { [sampleID: string]: Array<Sample> } {
    const specCount = this.mission.getSamplingSpecs().length;
    const groups = {};

    cleanSet(orderBy(flatten(this.mission.getSampleSites().map((site) => site.getSamples())), ['order'], ['asc']))
      .filter((sample) => !sample.skipped_or_deleted)
      .forEach((sample, index) => {
        const groupID = Math.floor(index / specCount);

        if (!groups[groupID]) {
          groups[groupID] = [];
        }

        groups[groupID].push(sample);
      });

    return groups;
  }

  private generateCores(
    sample: Sample,
    nextPointUtmLoc: UtmLocation | undefined,
    mission: Mission,
    initialPrevPoint: Coordinate | null,
    sampleUtmLocation: UtmLocation,
    subsamplesShape: BaseShape,
    prevSampleId: string,
  ) {
    // If the sample is not pending, remove it from the path and return.
    // We don't want to remove pulled cores and regenerate them.
    if (!sample.isPending()) {
      sample.removeFromPath();

      return;
    }

    // Get existing cores and their place in the path; dispose existing cores
    const cores = cleanSet(sample.getSoilCores());
    let waypointNumber = cleanSet(mission.getWaypoints()).length + 1;

    for (const core of cores) {
      const corePoint = core.getCorePoint();

      if (corePoint && corePoint.waypoint_number !== undefined && corePoint.waypoint_number < waypointNumber) {
        waypointNumber = corePoint.waypoint_number;
      }

      core.dispose();
    }

    // Determine how to enter the cluster.
    // Call with the points in the cluster, the previous sample point,
    // and the center point of the next cluster.
    const waypoints = cleanSet(mission.getWaypoints());
    const prevWaypoint = waypoints.find((sel) => sel.waypoint_number === waypointNumber - 1);
    const prevPoint = prevWaypoint
      ? fromLatLon(prevWaypoint.lat, prevWaypoint.lon)
      : initialPrevPoint
        ? fromLatLon(initialPrevPoint[0], initialPrevPoint[1])
        : null;

    const missionJob = mission.getJob();

    const { optimalPositions, choseToAlternate } = subsamplesShape.getOptimalCoresPositions(
      [sampleUtmLocation.easting, sampleUtmLocation.northing],
      prevPoint ? [prevPoint.easting, prevPoint.northing] : null,
      nextPointUtmLoc ? [nextPointUtmLoc.easting, nextPointUtmLoc.northing] : null,
      this.shouldAlternateDirection(missionJob!, prevSampleId, sample.sample_id),
      this.alternatedOnPreviousSample,
    );

    this.alternatedOnPreviousSample = choseToAlternate;

    // Generate the core pattern
    for (const [i, samplePoint] of optimalPositions.entries()) {
      // create core
      const [e, n] = samplePoint;
      const latLon = toLatLon(e, n, sampleUtmLocation.zoneNum, sampleUtmLocation.zoneLetter);
      const core = SoilCore.create();
      core.Sample_id = sample.instance_id;
      core.core_number = i + 1;
      core.lat = latLon.latitude;
      core.lon = latLon.longitude;

      // create new core waypoint
      const waypoint = Waypoint.create();
      waypoint.Mission_id = mission.instance_id!;
      waypoint.waypoint_number = waypointNumber + i; // Removing strage 0.001 * i logic because otherwise prevPoint is always undefined
      waypoint.lat = latLon.latitude;
      waypoint.lon = latLon.longitude;
      const corePoint = CorePoint.create();
      corePoint.Waypoint_id = waypoint.instance_id!;
      corePoint.SoilCore_id = core.instance_id!;
    }
  }

  private shouldAlternateDirection(
    missionJob: Job,
    prevSampleID: string,
    currentSampleID: string | undefined,
  ): boolean {
    // Can be true for multi-lab jobs
    if (prevSampleID === currentSampleID) {
      return false;
    }

    if (missionJob.plot_mission) {
      return false;
    }

    if (missionJob.disable_core_line_alteration) {
      return false;
    }

    return true;
  }

  private findIntermediateWaypoints(fromSampleId: string, toSampleId: string): Coordinate[] {
    if (typeof this.intermediateWaypointsMap[fromSampleId] === 'undefined') {
      return [];
    }

    if (typeof this.intermediateWaypointsMap[fromSampleId][toSampleId] === 'undefined') {
      return [];
    }

    return this.intermediateWaypointsMap[fromSampleId][toSampleId];
  }

  private getIntermediateWaypointsBidirectional(fromSampleId: string, toSampleId: string): Coordinate[] {
    let waypoints = this.findIntermediateWaypoints(fromSampleId, toSampleId);

    if (waypoints.length === 0) {
      // We are reversing the waypoints here because we are going in the opposite direction
      waypoints = this.findIntermediateWaypoints(toSampleId, fromSampleId).reverse();
    }

    return waypoints;
  }
}

export default GridPathGenerator;
